import { useState, useEffect, useRef } from 'react';
import { getGroupColorByName } from '../groupColors';

function formatPhoneDisplay(phone) {
  if (!phone) return '';
  if (phone.startsWith('+1')) {
    // US/CA: +12135550123 -> +1 213-555-0123
    const digits = phone.slice(2);
    return `+1 ${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (phone.startsWith('+33')) {
    // FR: +33612345678 -> +33 6 12 34 56 78
    const digits = phone.slice(3);
    return `+33 ${digits.slice(0, 1)} ${digits.slice(1, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 7)} ${digits.slice(7)}`;
  }
  if (phone.startsWith('+44')) {
    // UK: +447911123456 -> +44 791 112 3456
    const digits = phone.slice(3);
    return `+44 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  if (phone.startsWith('+49')) {
    // DE: +4915123456789 -> +49 1512 3456789
    const digits = phone.slice(3);
    return `+49 ${digits.slice(0, 4)} ${digits.slice(4)}`;
  }
  return phone;
}

export default function ContactLine({
  contact,
  trads,
  onNameChange,
  onSendVerification,
  onDelete,
  onVerify,
  onUpdate,
}) {
  const [name, setName] = useState(contact.name || '');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const debounceRef = useRef(null);

  const t = trads?.contacts || {};
  const isVerified = !!contact.verified;

  // Calculate linked events info for delete confirmation
  const linkedEvents = contact.eventContacts || [];
  const linkedEventCount = linkedEvents.length;
  const eventsWithOnlyThisContact = linkedEvents.filter(
    (ec) => ec.event?.eventContacts?.length === 1
  ).length;

  const handleDelete = () => {
    setShowDeleteConfirm(false);
    onDelete?.(contact.id);
  };

  useEffect(() => {
    setName(contact.name || '');
  }, [contact.name]);

  const handleNameChange = (e) => {
    const newName = e.target.value;
    setName(newName);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      onNameChange?.(contact.id, newName);
    }, 500);
  };

  const handleSendVerification = () => {
    onSendVerification?.(contact.id);
  };

  return (
    <div className="p-2 border border-gray-200 rounded-md relative">
      <div className="flex items-start relative justify-start flex-col">
        <div className="flex w-full items-start justify-between gap-3 mb-2">
          <p className="flex min-w-0 flex-1 flex-wrap items-baseline justify-start">
            <span className="text-lg font-bold text-gray-900">{name}</span>
            <span className="text-xs text-gray-500 ml-1">
              {linkedEventCount === 0
                ? t.linkedToNoEvents || 'linked to no events'
                : linkedEventCount === 1
                  ? t.linkedToOneEvent || 'linked to 1 event'
                  : (t.linkedToManyEvents || 'linked to {n} events').replace(
                      '{n}',
                      linkedEventCount
                    )}
            </span>
          </p>
          <div className="flex shrink-0 items-center gap-4 pt-1">
            <button
              type="button"
              onClick={() => onUpdate?.(contact)}
              data-testid="update-contact-btn"
              className="text-sm font-medium text-gray-900 underline hover:text-gray-700 cursor-pointer"
            >
              {t.update || 'Update'}
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              data-testid="delete-contact-btn"
              className="text-sm font-medium text-gray-900 underline hover:text-gray-700 cursor-pointer"
            >
              {t.delete || 'Delete'}
            </button>
          </div>
        </div>
        <div className="w-[200px] mb-1">
          <div className="flex flex-row items-center">
            <span className="text-sm font-medium text-gray-800">
              {contact.countryCode}
            </span>
            <span className="whitespace-pre ml-2 text-sm text-gray-800 font-semibold font-mono">
              {formatPhoneDisplay(contact.phone)}
            </span>
          </div>
        </div>
        {Array.isArray(contact.groupMemberships) &&
          contact.groupMemberships.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 w-full max-w-[560px] mt-1 mb-2">
              {contact.groupMemberships.map((group) => {
                const color = getGroupColorByName(group.color);
                return (
                  <div
                    key={group.id}
                    className="px-2 py-1 rounded-md text-xs font-medium"
                    style={{
                      backgroundColor: color.background,
                      color: color.text,
                    }}
                    data-testid={`contact-line-group-${group.id}`}
                  >
                    {group.name}
                  </div>
                );
              })}
            </div>
          )}
        {/* VERIFICATION UI DISABLED - Keeping code for future use
        <div className="flex-1">
          <input
            type="text"
            value={name}
            onChange={handleNameChange}
            placeholder={t.namePlaceholder || 'Contact name'}
            data-testid="contact-name-input"
            className="bg-white flex-1 min-w-[150px] max-w-[300px] px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          {isVerified ? (
            <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
              {t.verified || 'Verified'}
            </span>
          ) : (
            <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
              {t.notVerified || 'Not verified'}
            </span>
          )}
        </div>
        */}
        <div className="flex-1">
          <input
            type="text"
            value={name}
            onChange={handleNameChange}
            placeholder={t.namePlaceholder || 'Contact name'}
            data-testid="contact-name-input"
            className="bg-white flex-1 min-w-[150px] max-w-[300px] px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {t.deleteConfirmTitle || 'Delete contact?'}
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              {linkedEventCount > 0
                ? (
                    t.deleteConfirmMessage ||
                    'This contact is linked to {n} SMS event(s). After deletion, {m} event(s) will not have any contact to be sent to.'
                  )
                    .replace('{n}', linkedEventCount)
                    .replace('{m}', eventsWithOnlyThisContact)
                : t.deleteConfirmNoEvents ||
                  'This contact is not linked to any SMS events.'}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                data-testid="delete-cancel-btn"
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                {t.cancel || 'Cancel'}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                data-testid="delete-confirm-btn"
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
              >
                {t.deleteConfirm || 'Yes, delete contact'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* VERIFICATION UI DISABLED - Keeping code for future use
      {!isVerified && (
        <div className="mt-2 ml-0">
          <button
            type="button"
            onClick={() => onVerify?.(contact)}
            data-testid="verify-contact-btn"
            className="text-sm font-medium text-indigo-600 underline hover:text-indigo-800 cursor-pointer"
          >
            {t.verifyNow || 'Verify now'}
          </button>
        </div>
      )}
      */}
      {/* commented out, now passive opt-in system for US */}
      {!contact.optedInAt &&
        contact.countryCode === 'CA' &&
        contact.mostRecentOptInMessage && (
          <div className="mt-2 text-sm text-gray-500 bg-amber-100 text-yellow-700 p-2 rounded-md border border-yellow-400">
            <p>
              ⚠️{' '}
              {(
                t.optInWarningUS ||
                'Contact must reply "yes" to the SMS received from {phoneNumber}'
              ).replace(
                '{phoneNumber}',
                formatPhoneDisplay(
                  contact.mostRecentOptInMessage.senderPhone?.phoneNumber || ''
                )
              )}
            </p>
          </div>
        )}
    </div>
  );
}
